import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { formatDate, formatTime, useTimeFormat } from "helpers/timeFormat";

import { LoyaltyPoints, LoyaltyProgress, PlayerLoyaltyReward } from "store/players/list/slice";
import { playersListLimits } from "configs/limits";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import { useAppDispatch } from "store";
import { NotificationTypes, showNotification } from "store/notifications/slice";

import Pagination from "components/Pagination";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import EmptyState from "components/EmptyState/EmptyState";
import ImageWithFallback from "helpers/ImageWithFallback";
import Button from "components/buttons/Button";
import RoundBtn from "components/buttons/RoundBtn";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import rewardIcon from "assets/icons/reward-icon.png";
import styles from "./ListLoyaltyTab.module.scss";

const resolveProgressPercent = (level: LoyaltyProgress["level"]): number => {
  const current = Number(level.currentPoints) || 0;
  const end = Number(level.pointsToComplete) || 0;
  const start = Number(level.valueFrom) || 0;
  const span = Math.max(end - start, 0);
  if (span <= 0) {
    return current >= end ? 100 : 0;
  }
  return Math.min(Math.max(((current - start) / span) * 100, 0), 100);
};

const normalizePoints = (points: number): number => Number(points.toFixed(10));

const resolvePointsInsideLevel = (level: LoyaltyProgress["level"]): number => {
  const current = Number(level.currentPoints) || 0;
  const start = Number(level.valueFrom) || 0;
  return Math.max(normalizePoints(current - start), 0);
};

const resolvePointsToNext = (level: LoyaltyProgress["level"]): number => {
  const current = Number(level.currentPoints) || 0;
  const currentEnd = Number(level.pointsToComplete) || 0;
  return Math.max(normalizePoints(currentEnd - current), 0);
};

const SLIDER_REWARD_TYPES = new Set(["BONUS", "CASHBACK", "COINS", "RANKING"]);

const rewardSubtitle = (
  reward: PlayerLoyaltyReward,
  t: (key: string) => string,
): string => {
  if (reward.type === "COINS") return t("coinsReward");
  if (reward.type === "RANKING") return t("loyaltyRankingPoints");
  return reward.description ?? "";
};

const ListLoyaltyTab: React.FC = () => {
  const timeFormat = useTimeFormat();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const [list, setList] = useState<LoyaltyPoints[]>([]);
  const [currentItem, setCurrentItem] = useState<LoyaltyProgress>();
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [currentLimit, setCurrentLimit] = useState(playersListLimits[0]);
  const [isFetched, setIsFetched] = useState(false);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canScrollBack, setCanScrollBack] = useState(false);
  const [canScrollForward, setCanScrollForward] = useState(false);
  const [levelOptions, setLevelOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [selectedLevelId, setSelectedLevelId] = useState<number | "">("");
  const [pointsInput, setPointsInput] = useState("");
  const [savingCorrection, setSavingCorrection] = useState(false);

  const handleLoadList = async () => {
    setIsFetched(false);
    try {
      const result = await instance.get(
        `${apiRoutes.playerLoyalty}/${searchParams.get("entityId")}?limit=${currentLimit}&offset=${activePage - 1}`,
      );
      setList(result.data?.points?.items ?? []);
      setTotalItems(result.data?.points?.totalItems ?? 0);
      setTotalPages(result.data?.points?.totalPages ?? 0);
      setCurrentItem(result.data);
    } catch {
      setList([]);
      setTotalItems(0);
      setTotalPages(0);
      setCurrentItem(undefined);
      dispatch(
        showNotification({
          title: t("error"),
          message: t("unexpectedServerResponse"),
          type: NotificationTypes.error,
        }),
      );
    }
    setIsFetched(true);
  };

  useEffect(() => {
    handleLoadList();
  }, [activePage, currentLimit]);

  useEffect(() => {
    let cancelled = false;
    instance
      .post(apiRoutes.loyaltyLevels, { limit: 200, offset: 0 })
      .then((result) => {
        if (cancelled) return;
        const items = (result.data?.items ?? []) as {
          id: number;
          title?: string;
          levelNumber?: string | number;
          image?: string | null;
          isActive?: boolean | number;
        }[];
        setLevelOptions(
          items
            .filter((item) => item.isActive === true || item.isActive === 1)
            .map((item) => ({
              value: item.id,
              label: item.title || String(item.levelNumber ?? item.id),
              img: item.image || undefined,
            })),
        );
      })
      .catch(() => {
        if (!cancelled) setLevelOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const level = currentItem?.level;

  useEffect(() => {
    if (!level) return;
    setSelectedLevelId(level.loyaltyId);
    setPointsInput(String(resolvePointsInsideLevel(level)));
  }, [level?.loyaltyId, level?.currentPoints, level?.valueFrom]);
  const rewards = level?.rewards ?? [];
  const sliderRewards = rewards.filter((reward) => SLIDER_REWARD_TYPES.has(reward.type));
  const infoRewards = rewards.filter((reward) => reward.type === "INFO");
  const progress = level ? resolveProgressPercent(level) : 0;
  const levelCapacity = Number(level?.pointsRequired) || 0;
  const pointsInsideLevel = level ? resolvePointsInsideLevel(level) : 0;
  const nextThreshold = Number(level?.pointsToComplete) || 0;
  const pointsToNext = level ? resolvePointsToNext(level) : 0;

  const saveCorrection = async () => {
    const entityId = searchParams.get("entityId");
    if (!entityId || selectedLevelId === "" || pointsInput.trim() === "") return;
    setSavingCorrection(true);
    try {
      await instance.put(`${apiRoutes.playerLoyalty}/${entityId}`, {
        loyaltyLevelId: selectedLevelId,
        pointsInLevel: pointsInput.trim(),
      });
      await handleLoadList();
      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityUpdateSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } catch {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("unexpectedServerResponse"),
          type: NotificationTypes.error,
        }),
      );
    }
    setSavingCorrection(false);
  };

  const updateSliderArrows = () => {
    const node = scrollerRef.current;
    if (!node) {
      setCanScrollBack(false);
      setCanScrollForward(false);
      return;
    }
    setCanScrollBack(node.scrollLeft > 4);
    setCanScrollForward(node.scrollLeft + node.clientWidth < node.scrollWidth - 4);
  };

  useEffect(() => {
    updateSliderArrows();
    window.addEventListener("resize", updateSliderArrows);
    return () => window.removeEventListener("resize", updateSliderArrows);
  }, [sliderRewards.length, isFetched]);

  const scrollSlider = (direction: -1 | 1) => {
    scrollerRef.current?.scrollBy({ left: direction * 220, behavior: "smooth" });
  };

  return (
    <div className={`${styles.loyalty} player-content-wrapper`}>
      {level && (
        <section className={styles.overviewBlock}>
          <div className={`loyalty-progress ${styles.overview}`}>
            {level.image ? (
              <ImageWithFallback
                src={level.image}
                alt={level.title}
                width={40}
                height={40}
              />
            ) : (
              <div className={styles.badgeFallback} aria-hidden="true" />
            )}
            <div className="progress-bar">
              <div className="title">{level.title}</div>
              <div
                className="bar"
                style={{
                  width: `${progress}%`,
                }}
              ></div>
              <div className="points">
                {pointsInsideLevel} / {levelCapacity}
              </div>
            </div>
          </div>
          <div className={styles.stats}>
            <div className={styles.stat}>
              <div className={styles.statTitle}>{t("currentLevel")}</div>
              <div className={styles.statValue}>{level.title}</div>
            </div>
            <div className={styles.stat}>
              <div className={styles.statTitle}>{t("points")}</div>
              <div className={styles.statValue}>{pointsInsideLevel}</div>
            </div>
            <div className={styles.stat}>
              <div className={styles.statTitle}>{t("pointsToNext")}</div>
              <div className={styles.statValue}>{pointsToNext}</div>
            </div>
            <div className={styles.stat}>
              <div className={styles.statTitle}>{t("nextLoyaltyLevel")}</div>
              <div className={`${styles.statValue} ${styles.nextLevelValue}`}>
                {level.nextImage && (
                  <ImageWithFallback
                    src={level.nextImage}
                    alt={level.nextTitle || t("nextLoyaltyLevel")}
                    width={18}
                    height={18}
                  />
                )}
                <span>
                  {level.nextTitle ||
                    level.nextLoyaltyLevel ||
                    t("notAvailable")}
                </span>
              </div>
            </div>
            <div className={styles.stat}>
              <div className={styles.statTitle}>{t("pointsToNext")}</div>
              <div className={styles.statValue}>{nextThreshold}</div>
            </div>
          </div>
          <div className={styles.correction}>
            <DefaultDropdown
              label={t("currentLevel")}
              value={selectedLevelId}
              options={levelOptions}
              disabled={savingCorrection || levelOptions.length === 0}
              onSelect={(value) => setSelectedLevelId(Number(value))}
            />
            <InnerInput
              label={t("points")}
              value={pointsInput}
              type="text"
              id="loyalty-points-in-level"
              disabled={savingCorrection}
              onChange={(value) => setPointsInput(String(value))}
            />
            <Button
              txt={t("save")}
              isPrimary
              isSmall
              isLoad={savingCorrection}
              disabled={
                savingCorrection ||
                selectedLevelId === "" ||
                pointsInput.trim() === ""
              }
              handleClick={saveCorrection}
            />
          </div>
          <div className={styles.rewards}>
            <h3 className={styles.rewardsTitle}>{t("unlockedRewards")}:</h3>
            {rewards.length === 0 ? (
              <EmptyState message={t("noUnlockedRewards")} />
            ) : (
              <>
                {sliderRewards.length > 0 && (
                  <div className={styles.slider}>
                    {(canScrollBack || canScrollForward) && (
                      <div className={styles.sliderNav}>
                        <RoundBtn
                          txt="‹"
                          disabled={!canScrollBack}
                          handleClick={() => scrollSlider(-1)}
                        />
                        <RoundBtn
                          txt="›"
                          disabled={!canScrollForward}
                          handleClick={() => scrollSlider(1)}
                        />
                      </div>
                    )}
                    <div
                      className={styles.sliderTrack}
                      ref={scrollerRef}
                      onScroll={updateSliderArrows}
                    >
                      {sliderRewards.map((reward) => {
                        const subtitle = rewardSubtitle(reward, t);
                        return (
                          <article
                            className={styles.rewardCard}
                            key={`${reward.type}-${reward.id ?? reward.title ?? reward.amount}`}
                          >
                            <ImageWithFallback
                              src={reward.image || rewardIcon}
                              alt=""
                              width={36}
                              height={36}
                            />
                            <span className={styles.rewardType}>
                              {t(reward.type.toLowerCase())}
                            </span>
                            <span className={styles.rewardTitle}>
                              {reward.type === "COINS" || reward.type === "RANKING"
                                ? (reward.amount ?? t("notAvailable"))
                                : reward.title || t(reward.type.toLowerCase())}
                            </span>
                            {subtitle ? (
                              <span className={styles.rewardDescription}>{subtitle}</span>
                            ) : null}
                          </article>
                        );
                      })}
                    </div>
                  </div>
                )}
                {infoRewards.length > 0 && (
                  <div className={styles.infoList}>
                    {infoRewards.map((reward) => {
                      const available = reward.isAvailable !== false;
                      return (
                        <div
                          className={`${styles.infoRow} ${
                            available ? "" : styles.infoRowLocked
                          }`}
                          key={`info-${reward.id ?? reward.title}`}
                        >
                          <span className={styles.rewardType}>{t("info")}</span>
                          <div className={styles.infoCopy}>
                            <span className={styles.rewardTitle}>
                              {reward.title || t("info")}
                            </span>
                            {reward.description ? (
                              <span className={styles.rewardDescription}>
                                {reward.description}
                              </span>
                            ) : null}
                          </div>
                          <span
                            className={`badge ${
                              available ? "badge--success" : "badge--disabled"
                            }`}
                          >
                            {available ? t("available") : t("unavailable")}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      )}
      {isFetched && !level && (
        <EmptyState message={t("noLoyaltyProgress")} />
      )}
      {isFetched ? (
        <>
          <div className={`${styles.table} inner-table-wrapper`}>
            {list.length === 0 ? (
              <EmptyState message={t("noLoyaltyOperations")} />
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>{t("type")}</th>
                    <th>{t("points")}</th>
                    <th>{t("created")}</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((el) => (
                    <tr key={el.id}>
                      <td>{el.id}</td>
                      <td>{t(el.type)}</td>
                      <td>{el.points}</td>
                      <td className="date">
                        <span>{formatDate(el.created)}</span>
                        <span>{formatTime(el.created, timeFormat)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {list.length > 0 && (
            <Pagination
              activePage={activePage}
              limit={currentLimit}
              totalItems={totalItems}
              totalPages={totalPages}
              limitsArray={playersListLimits}
              handleLimitClick={(limit) => {
                setCurrentLimit(limit);
                setActivePage(1);
              }}
              handleNextClick={() => {
                if (activePage !== totalPages) {
                  setActivePage(activePage + 1);
                }
              }}
              handlePrevClick={() => {
                if (activePage > 1) {
                  setActivePage(activePage - 1);
                }
              }}
            />
          )}
        </>
      ) : (
        <div className={styles.table}>
          <ModalTabSkeleton />
        </div>
      )}
    </div>
  );
};

export default ListLoyaltyTab;
